import { vec2 } from "gl-matrix";
import { TOUCH_DRAG_THRESHOLD_PX } from "@/constants/UIConstants";
import { centroid, distance, midpoint } from "@/utils/math/Points.mjs";
import { getPinchZoom, getStagePosition, toDesignPoint } from "@/utils/ZoomUtils";

// What a running gesture does with its fingers.
/** Pans or zooms the stage. */
const GESTURE_STAGE = "stage";
/** Keeps the finger that opened the context menu, so it can't drag. */
const GESTURE_HOLD = "hold";
/** Three fingers sweeping sideways to undo or redo. */
const GESTURE_SWIPE = "swipe";

// What a stage gesture decided it is from its fingers' movement.
const INTENT_PAN = "pan";
const INTENT_ZOOM = "zoom";

/**
 * @typedef {import("@/utils/ZoomUtils").Viewport} Viewport
 * @typedef {typeof INTENT_PAN | typeof INTENT_ZOOM} StageIntentValue
 * @typedef {{ clientX: number, clientY: number }} ClientPoint
 */

/**
 * Pans or zooms the stage around a design point.
 * @typedef {Object} StageGesture
 * @prop {typeof GESTURE_STAGE} mode
 * @prop {StageIntentValue | null} intent - Null until the fingers' movement decides it.
 * @prop {Map<number, vec2>} starts - Each finger's client point when it began.
 * @prop {Viewport} viewport - The stage's viewport when it began.
 * @prop {{ x: number, y: number }} anchor - The design point it keeps in place.
 * @prop {ClientPoint} landed - The fingers' midpoint when it began.
 * @prop {number} startDistance - Between the fingers when it began.
 *
 * Keeps the finger that opened the context menu, so it can't drag.
 * @typedef {Object} HoldGesture
 * @prop {typeof GESTURE_HOLD} mode
 *
 * Three fingers sweeping sideways to undo or redo.
 * @typedef {Object} SwipeGesture
 * @prop {typeof GESTURE_SWIPE} mode
 * @prop {Map<number, vec2>} starts - Each finger's client point when it began.
 * @prop {boolean} swiped - Whether it has undone or redone yet; once per swipe.
 *
 * @typedef {"left" | "right"} SwipeDirection
 *
 * @typedef {StageGesture | HoldGesture | SwipeGesture} Gesture
 *
 * A first finger's press, held back until it is a drag, a tap or a hold.
 * @typedef {Object} HeldPress
 * @prop {Element} target - What it pressed.
 * @prop {PointerEvent} down
 * @prop {vec2} start - Its client point.
 * @prop {ReturnType<typeof setTimeout>} timer - Opens the context menu.
 *
 * @typedef {Object} StageGesturesProps
 * @prop {HTMLElement} stage - Pointers must land inside it.
 * @prop {() => boolean} isTransforming - A running transform keeps its finger;
 *   a second finger landing then starts no gesture.
 * @prop {() => boolean} canPan - False where the stage must not scroll (crop
 *   mode): two fingers still zoom, anchored where they landed.
 * @prop {() => Viewport} getViewport
 * @prop {(position: { zoom: number } & ReturnType<typeof getStagePosition>) => void} onStageGesture
 * @prop {(direction: SwipeDirection) => void} onSwipe - Three fingers swiped:
 *   left undoes, right redoes.
 */

/** How long a still finger is held before it opens the context menu. */
export const LONG_PRESS_MS = 500;

/** Screen px a finger must move before a two-finger stage gesture decides between pan and zoom. */
export const GESTURE_COMMIT_PX = 10;

/**
 * Cosine similarity of the two fingers' movements above which they are
 * panning together; at or below it they are pinching apart or together.
 */
export const PAN_MIN_SIMILARITY = 0;

/** Screen px three fingers' midpoint must travel sideways before they swipe. */
export const SWIPE_COMMIT_PX = 60;

/** Most a swipe may rise or fall per px it travels sideways. */
export const SWIPE_MAX_SLOPE = 0.5;

/** Most ms between two taps' lifts for them to double-tap. */
export const DOUBLE_TAP_MS = 300;

/** Most screen px between two taps for them to double-tap. */
export const DOUBLE_TAP_MAX_PX = 20;

/** Pointers this decides for; a mouse goes straight to the stage's own listeners. */
const TRACKED_POINTER_TYPES = ["touch", "pen"];

/**
 * @param {PointerEvent} e
 * @returns {vec2}
 */
const pointOf = (e) => vec2.fromValues(e.clientX, e.clientY);

/**
 * @param {vec2} point
 * @returns {ClientPoint}
 */
const toClient = ([clientX, clientY]) => ({ clientX, clientY });

/**
 * The keys held with a press, so its replay selects as the press would
 * (shift adds to the selection, alt duplicates).
 * @param {PointerEvent} e
 * @returns {Pick<PointerEventInit, "shiftKey" | "metaKey" | "ctrlKey" | "altKey">}
 */
const modifiersOf = ({ shiftKey, metaKey, ctrlKey, altKey }) => ({
  shiftKey,
  metaKey,
  ctrlKey,
  altKey,
});

/**
 * Touch and pen stage gestures: two-finger pan and zoom, long-press, and a
 * three-finger swipe to undo or redo.
 *
 * A finger or pen is otherwise handled like a mouse by the stage's own
 * pointer listeners (selection, transformer, marquee). This listens on the
 * window in the capture phase, ahead of all of them, and decides what a
 * touch is before they see it. A press is held back until the pointer moves
 * far enough to drag, lifts as a tap, or is held for the context menu; then
 * it is emitted at what it pressed, and the pointer is theirs. A second
 * finger landing first makes it a gesture, and the press never happened.
 * A gesture takes every finger: a press already emitted is cancelled with a
 * pointercancel, as the browser does when it takes over a touch, and from
 * then on no event from the gesture's fingers reaches anything else.
 * Pointers of another type than the first one down are left alone, so a pen
 * and a resting palm never make a two-finger gesture.
 * - Two fingers pan or pinch-zoom the stage, decided by the direction of
 *   their first movement each time two fingers are down: a pan keeps the
 *   design point under the midpoint following it, a zoom keeps it where the
 *   fingers landed.
 * - A pointer held still opens the context menu: a contextmenu event is
 *   dispatched at what it pressed, as a right-click would.
 * - Three fingers, the third landing before two have panned or zoomed, swipe
 *   left to undo or right to redo, once per swipe.
 * - Two taps close together dispatch a dblclick at what the second pressed,
 *   once its lift has passed. iPadOS sends none where touch-action leaves it
 *   no double-tap zoom, as on the stage. One the browser sends for a double
 *   tap this already dispatched for is stopped; any other passes, as an Apple
 *   Pencil's does: its second tap reaches the page only as click and dblclick.
 */
class StageGestures {
  #stage;
  #isTransforming;
  #canPan;
  #getViewport;
  #onStageGesture;
  #onSwipe;

  /** Client point of each tracked finger or pen. @type {Map<number, vec2>} */
  #fingers = new Map();
  /** The pointer type of the tracked pointers, from the first one down. */
  #pointerType = "";
  /** @type {Gesture | null} */
  #gesture = null;
  /** @type {HeldPress | null} */
  #held = null;
  /** The pointer whose press was emitted to the stage's own listeners. @type {number | null} */
  #emitted = null;
  /** An event this dispatches, so its own listeners let it pass. @type {Event | null} */
  #dispatched = null;
  #frame = 0;
  /** The last tap, which a tap close after it makes a double tap. @type {{ point: vec2, time: number } | null} */
  #lastTap = null;
  /** Dispatches a double tap's dblclick. @type {ReturnType<typeof setTimeout> | undefined} */
  #doubleTapTimer;
  /** When the last double tap's dblclick was dispatched. */
  #doubleTappedAt = -Infinity;
  /** Aborted on detach, removing the window listeners. @type {AbortController | null} */
  #listeners = null;

  /** @param {StageGesturesProps} props */
  constructor({ stage, isTransforming, canPan, getViewport, onStageGesture, onSwipe }) {
    this.#stage = stage;
    this.#isTransforming = isTransforming;
    this.#canPan = canPan;
    this.#getViewport = getViewport;
    this.#onStageGesture = onStageGesture;
    this.#onSwipe = onSwipe;
  }

  /** Listens on the window, ahead of the stage's own listeners. */
  attach() {
    this.#listeners = new AbortController();
    const options = { capture: true, signal: this.#listeners.signal };
    window.addEventListener("pointerdown", this.#onDown, options);
    window.addEventListener("pointermove", this.#onMove, options);
    window.addEventListener("pointerup", this.#onUp, options);
    window.addEventListener("pointercancel", this.#onUp, options);
    window.addEventListener("dblclick", this.#onDoubleClick, options);
  }

  /** Removes the listeners, ending a running gesture. */
  detach() {
    this.#listeners?.abort();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#doubleTapTimer);
    this.#lastTap = null;
    this.#clearHeld();
    this.#gesture = null;
  }

  // * pointer events

  /** @param {PointerEvent} e */
  #onDown = (e) => {
    const { target, pointerType } = e;
    if (e === this.#dispatched) return;
    if (!(target instanceof Element) || !this.#stage.contains(target)) return;
    if (!TRACKED_POINTER_TYPES.includes(pointerType)) return;
    if (this.#fingers.size && pointerType !== this.#pointerType) return;

    // a touch is implicitly captured by the node it pressed; release it so
    // moves target whatever is under the finger, as they do for a mouse
    target.releasePointerCapture?.(e.pointerId);
    this.#fingers.set(e.pointerId, pointOf(e));
    this.#pointerType = pointerType;

    if (this.#gesture) {
      e.stopPropagation();
      // a finger re-landed: a stage gesture decides pan or zoom afresh, so the
      // last one's intent doesn't outlive the fingers that made it
      if (this.#fingers.size === 2 && this.#gesture.mode === GESTURE_STAGE) this.#anchorStage();
      // a third finger always lands after two have started a stage gesture;
      // landing before they pan or zoom makes it a swipe
      else if (
        this.#fingers.size === 3 &&
        this.#gesture.mode === GESTURE_STAGE &&
        this.#gesture.intent === null
      ) {
        this.#startSwipe();
      }
    } else if (this.#fingers.size === 1) {
      e.stopPropagation();
      this.#held = {
        target,
        down: e,
        start: pointOf(e),
        timer: setTimeout(this.#openContextMenu, LONG_PRESS_MS),
      };
    } else if (this.#fingers.size === 2 && !this.#isTransforming()) {
      e.stopPropagation();
      this.#takeOver();
      this.#anchorStage();
    }
  };

  /** @param {PointerEvent} e */
  #onMove = (e) => {
    const finger = this.#fingers.get(e.pointerId);
    if (!finger) return;
    vec2.set(finger, e.clientX, e.clientY);

    const held = this.#held;
    if (held?.down.pointerId === e.pointerId) {
      // a resting finger drifts, so it stays a press until it moves far enough to drag
      if (distance(finger, held.start) <= TOUCH_DRAG_THRESHOLD_PX) {
        e.stopPropagation();
        return;
      }
      this.#lastTap = null;
      this.#emit(held);
    }

    if (!this.#gesture) return;
    e.stopPropagation();
    this.#frame ||= requestAnimationFrame(this.#step);
  };

  /** @param {PointerEvent} e */
  #onUp = (e) => {
    if (e === this.#dispatched || !this.#fingers.delete(e.pointerId)) return;

    const held = this.#held;
    if (held?.down.pointerId === e.pointerId) {
      // a cancelled touch was never pressed, so nothing is told it ended
      if (e.type === "pointercancel") {
        this.#clearHeld();
        e.stopPropagation();
        return;
      }
      // a tap: its press arrives just before its lift
      this.#emit(held);
      this.#tapped(held);
    }
    if (this.#emitted === e.pointerId) this.#emitted = null;

    if (!this.#gesture) return;
    e.stopPropagation();
    // the gesture holds until every finger lifts, so a lagging finger can't drag
    if (this.#fingers.size === 0) this.#gesture = null;
  };

  // * presses

  #clearHeld() {
    clearTimeout(this.#held?.timer);
    this.#held = null;
  }

  /**
   * Emits a held press at what it pressed: the pointer is the stage's from now on.
   * @param {HeldPress} held
   */
  #emit({ target, down }) {
    this.#clearHeld();
    this.#emitted = down.pointerId;
    this.#dispatch(
      target,
      new PointerEvent("pointerdown", {
        bubbles: true,
        cancelable: true,
        composed: true,
        pointerId: down.pointerId,
        pointerType: down.pointerType,
        isPrimary: down.isPrimary,
        clientX: down.clientX,
        clientY: down.clientY,
        screenX: down.screenX,
        screenY: down.screenY,
        button: down.button,
        buttons: down.buttons,
        pressure: down.pressure,
        width: down.width,
        height: down.height,
        ...modifiersOf(down),
      }),
    );
  }

  /**
   * A tap after one close before it is a double tap: once the lift has
   * passed, a dblclick at what the second pressed.
   * @param {HeldPress} held
   */
  #tapped({ target, down, start }) {
    const now = performance.now();
    const last = this.#lastTap;
    const isDouble =
      last && now - last.time <= DOUBLE_TAP_MS && distance(start, last.point) <= DOUBLE_TAP_MAX_PX;
    if (!isDouble) {
      this.#lastTap = { point: start, time: now };
      return;
    }
    this.#lastTap = null;
    clearTimeout(this.#doubleTapTimer);
    this.#doubleTapTimer = setTimeout(() => {
      if (!target.isConnected) return;
      this.#doubleTappedAt = performance.now();
      this.#dispatch(
        target,
        new MouseEvent("dblclick", {
          bubbles: true,
          cancelable: true,
          composed: true,
          detail: 2,
          clientX: down.clientX,
          clientY: down.clientY,
          screenX: down.screenX,
          screenY: down.screenY,
          ...modifiersOf(down),
        }),
      );
    });
  }

  /** @param {MouseEvent} e */
  #onDoubleClick = (e) => {
    if (e === this.#dispatched) return;
    if (!(e.target instanceof Element) || !this.#stage.contains(e.target)) return;
    // the browser's own for a double tap this already dispatched one for
    if (performance.now() - this.#doubleTappedAt <= DOUBLE_TAP_MS) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    // the browser recognised the double tap itself (an Apple Pencil's second
    // tap sends it no pointer events), so its dblclick is the only one
    this.#lastTap = null;
    clearTimeout(this.#doubleTapTimer);
  };

  /** Takes the fingers for a gesture: a held press never happens, an emitted one is cancelled. */
  #takeOver() {
    this.#lastTap = null;
    this.#clearHeld();
    if (this.#emitted === null) return;
    this.#dispatch(
      window,
      new PointerEvent("pointercancel", {
        pointerId: this.#emitted,
        pointerType: this.#pointerType,
        isPrimary: true,
      }),
    );
    this.#emitted = null;
  }

  /** The held pointer stayed still: press, as a right-click does, then open the menu. */
  #openContextMenu = () => {
    const held = this.#held;
    if (!held) return;
    this.#emit(held);
    this.#takeOver();
    this.#gesture = { mode: GESTURE_HOLD };
    held.target.dispatchEvent(
      new MouseEvent("contextmenu", {
        bubbles: true,
        cancelable: true,
        clientX: held.down.clientX,
        clientY: held.down.clientY,
        ...modifiersOf(held.down),
      }),
    );
  };

  /**
   * @param {EventTarget} target
   * @param {Event} event - Passes this's own listeners.
   */
  #dispatch(target, event) {
    this.#dispatched = event;
    target.dispatchEvent(event);
    this.#dispatched = null;
  }

  // * stage gesture

  /** Anchors a stage gesture at the fingers' midpoint, its pan or zoom undecided. */
  #anchorStage() {
    const viewport = this.#getViewport();
    const landed = this.#midpoint();
    this.#gesture = {
      mode: GESTURE_STAGE,
      intent: null,
      starts: this.#snapshotFingers(),
      viewport,
      anchor: toDesignPoint(landed, viewport),
      landed,
      startDistance: this.#distance(),
    };
  }

  /** Applies the fingers' movement once per frame, to a stage gesture or a swipe. */
  #step = () => {
    this.#frame = 0;
    const gesture = this.#gesture;
    if (gesture?.mode === GESTURE_STAGE && this.#fingers.size === 2) this.#updateStage(gesture);
    else if (gesture?.mode === GESTURE_SWIPE && !gesture.swiped) this.#updateSwipe(gesture);
  };

  /**
   * A pan keeps the anchor under the fingers; a zoom keeps it where they landed.
   * @param {StageGesture} gesture
   */
  #updateStage(gesture) {
    gesture.intent ??= this.#stageIntent(gesture);
    const { intent, viewport, anchor, landed, startDistance } = gesture;
    if (intent === INTENT_PAN) {
      const point = this.#canPan() ? this.#midpoint() : landed;
      this.#onStageGesture({ zoom: viewport.zoom, ...getStagePosition(anchor, point, viewport) });
    } else if (intent === INTENT_ZOOM) {
      const zoom = getPinchZoom(viewport.zoom, startDistance, this.#distance());
      this.#onStageGesture({ zoom, ...getStagePosition(anchor, landed, viewport) });
    }
  }

  /**
   * What an undecided stage gesture is doing, from the cosine similarity of
   * the fingers' movements since it began: the same way is a pan, opposite
   * ways a pinch. A finger held still while the other moves is a pinch.
   * @param {StageGesture} gesture
   * @returns {StageIntentValue | null} Null until a finger has moved GESTURE_COMMIT_PX.
   */
  #stageIntent({ starts }) {
    const [a, b] = [...starts].map(([id, start]) =>
      vec2.subtract(vec2.create(), this.#fingers.get(id) ?? start, start),
    );
    const lengthA = vec2.length(a);
    const lengthB = vec2.length(b);
    const farthest = Math.max(lengthA, lengthB);
    const tooSoonToTell = farthest <= GESTURE_COMMIT_PX;
    // iOS can deliver one finger's first moves before the other's
    const otherMayBeLagging = Math.min(lengthA, lengthB) === 0 && farthest <= 2 * GESTURE_COMMIT_PX;
    if (tooSoonToTell || otherMayBeLagging) return null;

    // a still finger has no direction; vec2.angle would call it perpendicular
    const similarity = lengthA && lengthB ? vec2.dot(a, b) / (lengthA * lengthB) : -1;
    return similarity > PAN_MIN_SIMILARITY ? INTENT_PAN : INTENT_ZOOM;
  }

  // * swipe

  /** Turns the running gesture into a swipe from where the fingers are now. */
  #startSwipe() {
    this.#gesture = {
      mode: GESTURE_SWIPE,
      starts: this.#snapshotFingers(),
      swiped: false,
    };
  }

  /**
   * Undoes or redoes once the fingers' midpoint has gone far enough sideways.
   * @param {SwipeGesture} gesture
   */
  #updateSwipe(gesture) {
    const { starts } = gesture;
    const begun = [...starts.values()];
    // a lifted finger counts as still where it began, so the rest must travel further
    const now = [...starts].map(([id, start]) => this.#fingers.get(id) ?? start);
    const [dx, dy] = vec2.subtract(vec2.create(), centroid(now), centroid(begun));
    if (Math.abs(dx) < SWIPE_COMMIT_PX || Math.abs(dy) > Math.abs(dx) * SWIPE_MAX_SLOPE) return;

    gesture.swiped = true;
    this.#onSwipe(dx < 0 ? "left" : "right");
  }

  // * geometry

  /**
   * Where each finger is now, kept as where the gesture it starts began.
   * @returns {Map<number, vec2>}
   */
  #snapshotFingers() {
    return new Map([...this.#fingers].map(([id, point]) => [id, vec2.clone(point)]));
  }

  /**
   * Between the two fingers.
   * @returns {ClientPoint}
   */
  #midpoint() {
    const [a, b] = this.#fingers.values();
    return toClient(midpoint(a, b));
  }

  /**
   * Between the two fingers.
   * @returns {number}
   */
  #distance() {
    const [a, b] = this.#fingers.values();
    return distance(a, b);
  }
}

export default StageGestures;
